import { useActor } from '@xstate/react';
import React from 'react';
import Loader from '../components/loader/Loader';
import { EntryPointEvents, EntryPointMachineContext } from '../state-machine/types/entrypointMachine.types';
import DebitCardError from '../pages/debitCardError/debitCardError';
import Declined from '../pages/declined/Declined';
import ErrorsFound from '../pages/errorsFound/ErrorsFound';
import InternalError from '../pages/internalError/InternalError';
import OTPRequest from '../pages/otpRequest/OTPRequest';
import PreApproval from '../pages/pre-approval/PreApproval';
import { getSegmentEventEmitters } from '../state-machine/actions/segment';
import { EcommMachineMachineContext } from '../state-machine/types/ecommMachine.types';
import { useTranslation } from 'react-i18next';
import { selectLeaseHelper } from '../helpers';
import { FLAG_OTP_BYPASS_WHITELIST, FLAG_SHOW_DEBIT_DECISION_SHORT_FORM } from '../constants/feature-flags';

type FlowContainerProps = {
  parentMachine: any;
  setHeaderGoBackFunc: React.Dispatch<React.SetStateAction<(() => void) | undefined>>;
  setCurrentNode: (state: [string] | undefined) => void;
};

const EntryPointFlow = ({ parentMachine, setHeaderGoBackFunc, setCurrentNode }: FlowContainerProps) => {
  const { t } = useTranslation();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [parentState, parentTransition, parentInterpreter] = parentMachine;
  const parentContext = parentState.context as EcommMachineMachineContext;

  const [entryPointState, entryPointSend] = useActor<any, any>(parentState.children['EntryPointMachine']);
  const entryPointContext = entryPointState.context as EntryPointMachineContext;

  const segmentEventEmitters = getSegmentEventEmitters(entryPointSend);

  switch (true) {
    case entryPointState.hasTag('Loading'):
      return <Loader source="entrypoint" />;
    case entryPointState.matches('OTPRequest'):
      return (
        <OTPRequest
          onNext={entryPointSend}
          isLoading={entryPointState.hasTag('calling')}
          application={entryPointContext.application}
          orderDetails={entryPointContext.orderDetails}
          dealer={entryPointContext.dealer}
          cellPhone={entryPointContext.cellPhone!}
          role={parentContext.role}
          otpBypassWhitelisted={parentContext.ldClient.variation(FLAG_OTP_BYPASS_WHITELIST, false)}
          {...segmentEventEmitters}
        />
      );
    case entryPointState.matches('PreApproval'):
      return (
        <PreApproval
          onNext={entryPointSend}
          isLoading={entryPointState.hasTag('callingAuthorizeCard')}
          orderId={entryPointContext.orderId}
          application={entryPointContext.application}
          dealer={entryPointContext.dealer}
          role={parentContext?.role}
          onCompleteAppLater={() => entryPointSend({ type: EntryPointEvents.COMPLETE_APP_LATER })}
          orderDetails={entryPointContext.orderDetails}
          applyErrors={[]}
          {...segmentEventEmitters}
        />
      );
    case entryPointState.matches('BankAccountError'):
      return (
        <ErrorsFound
          body={t(($) => $.componentEntryPointFlow.bankAccountError)}
          buttonText={t(($) => $.componentEntryPointFlow.updateBankAccount)}
          buttonBehavior={() => entryPointSend({ type: EntryPointEvents.NEXT })}
          {...segmentEventEmitters}
        />
      );
    case entryPointState.matches('DebitCardError'):
      const ipAmount = selectLeaseHelper(entryPointContext.application?.lease?.options)?.applicationFee;
      const showDetailedDebitSoftDeclineMessages = parentContext.ldClient.variation(FLAG_SHOW_DEBIT_DECISION_SHORT_FORM, false);
      return (
        <DebitCardError
          onNext={() => entryPointSend({ type: EntryPointEvents.NEXT })}
          decisionCode={entryPointContext.application?.lease?.decisionCode}
          ipAmount={ipAmount!}
          showDetailedDebitSoftDeclineMessages={showDetailedDebitSoftDeclineMessages}
          {...segmentEventEmitters}
        />
      );
    case entryPointState.matches('LeaseDeclined'):
      return (
        <Declined
          orderId={entryPointContext.orderId}
          dealer={entryPointContext.dealer}
          application={entryPointContext.application}
          body={t(($) => $.componentEntryPointFlow.leaseDeclinedBody)}
          trackDetails={{ title: 'Application Declined' }}
          additionalBody={t(($) => $.componentEntryPointFlow.leaseDeclinedAdditionalBody)}
          setHeaderGoBackFunc={setHeaderGoBackFunc}
          {...segmentEventEmitters}
        />
      );
    case entryPointState.matches('LoanDeclined'):
      return (
        <Declined
          orderId={entryPointContext.orderId}
          dealer={entryPointContext.dealer}
          application={entryPointContext.application}
          body={t(($) => $.componentEntryPointFlow.loanDeclinedBody)}
          trackDetails={{ title: 'Loan Denied' }}
          additionalBody={t(($) => $.componentEntryPointFlow.loanDeclinedAdditionalBody)}
          setHeaderGoBackFunc={setHeaderGoBackFunc}
          {...segmentEventEmitters}
        />
      );
    default:
      return <InternalError orderId={entryPointContext.orderId} dealer={entryPointContext.dealer} role={parentContext?.role} {...segmentEventEmitters} />;
  }
};

export default EntryPointFlow;
